import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  RadioGroup,
  Radio
} from '@mui/material';
import { Loading } from '@bumble/app/components/Loading';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PageLoading } from '@bumble/app/components/PageLoading';

interface EthernetDeviceCreateProps {
  handleClose: () => void;
}

export const EthernetDeviceCreate = ({ handleClose }: EthernetDeviceCreateProps) => {
  const api = useApi();

  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [metroName, setMetroName] = useState<string>('');
  const [productCode, setProductCode] = useState<string>('');
  const [shippingAddress, setShippingAddress] = useState<string>('');
  const [siteAddress, setSiteAddress] = useState<string>('');
  const [providerServiceID, setProviderServiceID] = useState<string>('');
  const [clientCircuitID, setClientCircuitID] = useState<string>('');
  const [localContactName, setLocalContactName] = useState<string>('');
  const [localContactEmail, setLocalContactEmail] = useState<string>('');
  const [vlan, setVlan] = useState<boolean>(false);
  const [wanDataVlanId, setWanDataVlanId] = useState<string>('');
  const [qInQVlan, setQInQVlan] = useState<boolean>(false);
  const [wanQInQVlanId, setWanQInQVlanId] = useState<string>('');
  const [mgmtVlanId, setMgmtVlanId] = useState<string>('100');
  const [cirMbps, setCirMbps] = useState<string>('');
  const [eirMbps, setEirMbps] = useState<string>('');
  const [wan_sfp, setWanSFP] = useState<string>('sfp_mmf');
  const [lan_sfp, setLanSFP] = useState<string>('sfp_mmf');
  const [useWanSFP, setUseWanSFP] = useState<boolean>(false);
  const [useLanSFP, setUseLanSFP] = useState<boolean>(false);

  const metroPost = useMutation(api.createMetroApiV1CreateMetroPost, {
    onSuccess() {
      toast.success('Metro Device created.');
      handleClose();
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      if (Array.isArray((error as any).error.detail)) {
        (error as any).error.detail.forEach((el: any) =>
          toast.error(el.message, { autoClose: false, hideProgressBar: true }),
        );
      } else {
        toast.error((error as any).error.detail, { autoClose: false, hideProgressBar: true });
      }
    }
  })

  const { isLoading, data: productCodeList } = useQuery(
    ['metroProducts'],
    api.metroProductsApiV1MetroProductsGet.bind(null, {}),
    { enabled: true },
  );

  useEffect(() => {
    if (productCodeList && Array.isArray(productCodeList.data)) {
      setProductCode(productCodeList.data[0])
    }
  }, [productCodeList])

  const handleSubmit = (): void => {
    setLoadingModalOpen(true);
    metroPost.mutate({
      metro_name: metroName,
      prod_code: productCode,
      shipping_address: shippingAddress,
      site_address: siteAddress,
      provider_service_id: providerServiceID,
      client_circuit_id: clientCircuitID,
      contact_name: localContactName,
      contact_email: localContactEmail,
      ...(vlan && {
        wan_data_vlan_id: Number(wanDataVlanId),
      }),
      ...(qInQVlan && {
        wan_q_in_q_vlan_id: Number(wanQInQVlanId),
      }),
      wan_mgmt_vlan_id: Number(mgmtVlanId),
      wan_cir_mbps: Number(cirMbps),
      wan_eir_mbps: Number(eirMbps),
      ...(useWanSFP && { wan_sfp }),
      ...(useLanSFP && { lan_sfp }),
    });
  };

  const isDisabled = (): boolean => {
    const def = !(metroName && productCode);
    if (vlan)
      return isNaN(Number(wanDataVlanId)) || def;
    if (qInQVlan)
      return isNaN(Number(wanQInQVlanId)) || def;
    return def;
  };

  if (isLoading) return <Loading />;
  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            id="metro_name"
            label="Metro Name"
            variant="outlined"
            value={metroName}
            onChange={(e) => setMetroName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="product_code_label">Product Code</InputLabel>
            <Select
              labelId="product_code_label"
              id="product_code"
              label="Product Code"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
            >
              {productCodeList?.data.map((code: string) => (
                <MenuItem value={code} key={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="shipping_address"
            label="Shipping Address"
            variant="outlined"
            value={shippingAddress}
            onChange={(e) => setShippingAddress(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="site_address"
            label="Site Address"
            variant="outlined"
            value={siteAddress}
            onChange={(e) => setSiteAddress(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="provider_service_id"
            label="Provider Service ID"
            variant="outlined"
            value={providerServiceID}
            onChange={(e) => setProviderServiceID(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="client_circuit_id"
            label="Client Circuit ID"
            variant="outlined"
            value={clientCircuitID}
            onChange={(e) => setClientCircuitID(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="contact_name"
            label="Local Contact Name"
            variant="outlined"
            value={localContactName}
            onChange={(e) => setLocalContactName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="contact_email"
            label="Local Contact Email"
            variant="outlined"
            value={localContactEmail}
            onChange={(e) => setLocalContactEmail(e.target.value)}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 10 }} />
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" sx={{ mb: 4, display: 'inline-block', marginRight: '10px' }}>
          WAN Interface (UNI):
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={useWanSFP}
              onChange={(e) => setUseWanSFP(e.target.checked)}
            />
          }
          label="Use SFP Port (SFP0)"
        />
        {useWanSFP && (
          <FormControl component="fieldset" sx={{ mt: 2, display:'flex', paddingLeft: '170px' }}>
            <RadioGroup
              row
              value={wan_sfp}
              onChange={(e) => setWanSFP(e.target.value)}
            >
              <FormControlLabel value="sfp_mmf" control={<Radio />} label="Multimode Fiber (MMF) (Short range)" />
              <FormControlLabel value="sfp_smf" control={<Radio />} label="Single-Mode Fiber (SMF) (Long range)" />
            </RadioGroup>
          </FormControl>)
        }
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={vlan}
                onChange={(e) => setVlan(e.target.checked)}
              />
            }
            label="Enable VLAN tagging"
          />
        </Grid>
        {vlan && <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_data_vlan_id"
            label="User Data VLAN ID"
            variant="outlined"
            value={wanDataVlanId}
            onChange={(e) => setWanDataVlanId(e.target.value)}
          />
        </Grid>}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={qInQVlan}
                onChange={(e) => setQInQVlan(e.target.checked)}
              />
            }
            label="Enable Q-in-Q VLAN Tagging"
          />
        </Grid>
        {qInQVlan && <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_q_in_q_vlan_id"
            label="Q-in-Q VLAN ID"
            variant="outlined"
            value={wanQInQVlanId}
            onChange={(e) => setWanQInQVlanId(e.target.value)}
          />
        </Grid>}
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_cir_mbps"
            label="CIR (Committed Information Rate in Mbps)"
            variant="outlined"
            value={cirMbps}
            onChange={(e) => setCirMbps(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_eir_mbps"
            label="EIR (Exceeded Information Rate in Mbps)"
            variant="outlined"
            value={eirMbps}
            onChange={(e) => setEirMbps(e.target.value)}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 10 }} />
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" sx={{ mb: 4, display: 'inline-block', marginRight: '55px' }}>
          LAN Interface:
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={useLanSFP}
              onChange={(e) => setUseLanSFP(e.target.checked)}
            />
          }
          label="Use SFP Port (SFP1)"
        />
        {useLanSFP && (
          <FormControl component="fieldset" sx={{ mt: 2, display:'flex', paddingLeft: '180px' }}>
            <RadioGroup
              row
              value={lan_sfp}
              onChange={(e) => setLanSFP(e.target.value)}
            >
              <FormControlLabel
                value="sfp_mmf"
                control={<Radio />}
                label="Multimode Fiber (MMF) (Short range)"
              />
              <FormControlLabel
                value="sfp_smf"
                control={<Radio />}
                label="Single-Mode Fiber (SMF) (Long range)"
              />
            </RadioGroup>
          </FormControl>
        )}
      </Grid>
      <Divider sx={{ my: 10 }} />
      <Typography variant="body2" sx={{ mb: 8 }}>
        Management VLAN
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_mgmt_vlan_id"
            label="Mgmt VLAN ID"
            variant="outlined"
            value={mgmtVlanId}
            onChange={(e) => setMgmtVlanId(e.target.value)}
          />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="space-between" sx={{ mt: 10 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          onClick={handleSubmit}
          disabled={isDisabled()}
        >
          Create
        </Button>
      </Box>
      {loadingModalOpen && <PageLoading content="Working on it..." />}
    </Box>
  )
}
